/**
 * The queue's two hard moments: an operator editing something that has not synced (OFF-005), and the
 * server's answer coming back, complete or not (OFF-006, OFF-014). Pure, like queue.ts.
 */
import type { CommandEnvelope } from "../events/envelope.js";
import { type CommandQueue, enqueue, type LocalAuditEntry, type QueueOutcome } from "./queue.js";

/**
 * OFF-005: a queued command cannot be edited in place. Editing appends a replacement and marks the original
 * AMENDED; the pair is described by a LocalAuditEntry (before, after, who, why, device evidence) that is sent
 * with the queue — so the edit is in the record even though the first draft never reached the server.
 * Only a QUEUED command can be amended: once sent, the server owns it.
 */
export function amend(
  queue: CommandQueue,
  clientCommandId: string,
  replacement: CommandEnvelope,
  audit: Pick<LocalAuditEntry, "auditId" | "reason" | "actorUserId" | "deviceTime" | "monotonicNs">,
): QueueOutcome {
  const entry = queue.find((c) => c.clientCommandId === clientCommandId);
  if (!entry)
    return { ok: false, code: "NOT_FOUND", message: `no queued command with id ${clientCommandId}` };
  if (entry.state !== "QUEUED")
    return {
      ok: false,
      code: "WRONG_STATE",
      message: `command ${clientCommandId} is ${entry.state}; only a command that has not been sent can be amended`,
    };
  if (audit.reason.trim() === "")
    return {
      ok: false,
      code: "WRONG_STATE",
      message: "an edit before sync needs a reason (21 CFR 11.10(e))",
    };
  if (queue.some((c) => c.clientCommandId === replacement.clientCommandId))
    return { ok: false, code: "WRONG_STATE", message: "the replacement must carry a new command id" };

  const entryAudit: LocalAuditEntry = {
    ...audit,
    action: "AMEND",
    batchId: entry.batchId,
    clientCommandId,
    replacedByClientCommandId: replacement.clientCommandId,
    before: entry.envelope.payload,
    after: replacement.payload,
  };
  const marked = queue.map((c) =>
    c.clientCommandId === clientCommandId ? { ...c, state: "AMENDED" as const, localAudit: entryAudit } : c,
  );
  const next = enqueue(marked, replacement, entry.device).map((c) =>
    c.clientCommandId === replacement.clientCommandId ? { ...c, amends: clientCommandId } : c,
  );
  return { ok: true, queue: next };
}

/** Every local audit entry the queue holds — sent with each sync; the server keeps each one once. */
export function localAuditOf(queue: CommandQueue): LocalAuditEntry[] {
  return queue.flatMap((c) => (c.localAudit ? [c.localAudit] : []));
}

/** What the server said about one command it was sent. */
export interface SyncResult {
  readonly clientCommandId: string;
  readonly accepted: boolean;
  readonly code?: string;
  readonly message?: string;
}

/**
 * Applies the server's verdicts. A SENT command the server did not answer is NOT dropped and NOT assumed
 * accepted (OFF-006): it goes back to QUEUED to be sent again — safe, because replay is idempotent (OFF-007) —
 * and is reported as `missing`. A refusal is kept with its reason as a blocking task (OFF-014).
 */
export function applySyncResults(
  queue: CommandQueue,
  results: readonly SyncResult[],
): { queue: CommandQueue; missing: string[] } {
  const byId = new Map(results.map((r) => [r.clientCommandId, r]));
  const missing: string[] = [];
  const next = queue.map((c) => {
    if (c.state !== "SENT") return c;
    const r = byId.get(c.clientCommandId);
    if (!r) {
      missing.push(c.clientCommandId);
      return { ...c, state: "QUEUED" as const };
    }
    if (r.accepted) return { ...c, state: "ACCEPTED" as const };
    return {
      ...c,
      state: "REJECTED" as const,
      rejection: { code: r.code ?? "REFUSED", message: r.message ?? "refused by the server" },
    };
  });
  return { queue: next, missing };
}

/**
 * OFF-006: the server closed a sync session and named commands it never saw. Whatever this device believes
 * about them, they are not on the server, so they go back to QUEUED and are sent again (idempotent — OFF-007).
 */
export function requeueMissing(queue: CommandQueue, missingIds: readonly string[]): CommandQueue {
  const missing = new Set(missingIds);
  return queue.map((c) => {
    if (!missing.has(c.clientCommandId) || c.state === "AMENDED") return c;
    const { rejection: _dropped, ...rest } = c;
    return { ...rest, state: "QUEUED" as const };
  });
}

/** The ids a sync session must account for: every command about to be sent, in send order. */
export function manifestOf(queue: CommandQueue): string[] {
  return queue
    .filter((c) => c.state === "QUEUED" || c.state === "SENT")
    .sort((a, b) => a.sequence - b.sequence)
    .map((c) => c.clientCommandId);
}
